import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCustomer } from "@/context/CustomerContext";
import { toast } from "sonner";
import { User, Mail, Lock, Phone, Gift, Loader2, Calendar, Building2, Eye, EyeOff, ArrowLeft, KeyRound } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerAuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, isLoggedIn, loading } = useCustomer();
  const [isLogin, setIsLogin] = useState(searchParams.get("tab") !== "register");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [accountType, setAccountType] = useState("private"); // "private" o "business"
  
  // Reset password states
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: code + new password
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    birthdate: "",
    // Campi aziendali
    company_name: "",
    vat_number: "",
    fiscal_code: "",
    pec: "",
    sdi_code: "",
    company_address: ""
  });
  
  // Honeypot anti-bot
  const [honeypot, setHoneypot] = useState("");
  
  // Consensi GDPR
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);

  // Azienda partner
  const [partnerCompanies, setPartnerCompanies] = useState([]);
  const [hasPartnerCompany, setHasPartnerCompany] = useState(false);
  const [selectedPartnerCompanyId, setSelectedPartnerCompanyId] = useState("");

  useEffect(() => {
    if (!isLogin) {
      axios.get(`${API}/partner-companies`).then(res => {
        setPartnerCompanies(res.data);
      }).catch(() => {});
    }
  }, [isLogin]);

  useEffect(() => {
    if (!loading && isLoggedIn) {
      navigate("/profilo");
    }
  }, [loading, isLoggedIn, navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Anti-bot: se il campo honeypot è compilato, è un bot
    if (honeypot) {
      toast.error("Si è verificato un errore. Riprova.");
      return;
    }
    
    setSubmitting(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Bentornato!");
        navigate("/profilo");
      } else {
        if (!formData.name || !formData.phone) {
          toast.error("Compila tutti i campi obbligatori");
          setSubmitting(false);
          return;
        }
        if (accountType === "private" && !formData.fiscal_code) {
          toast.error("Il Codice Fiscale è obbligatorio per i clienti privati");
          setSubmitting(false);
          return;
        }
        if (!consentPrivacy) {
          toast.error("Devi accettare la Privacy Policy per registrarti");
          setSubmitting(false);
          return;
        }
        if (accountType === "business" && (!formData.company_name || !formData.vat_number)) {
          toast.error("Inserisci ragione sociale e P.IVA");
          setSubmitting(false);
          return;
        }
        if (hasPartnerCompany && !selectedPartnerCompanyId) {
          toast.error("Seleziona l'azienda partner di appartenenza");
          setSubmitting(false);
          return;
        }
        const result = await register(
          formData.name, 
          formData.email, 
          formData.password, 
          formData.phone, 
          formData.birthdate,
          accountType,
          accountType === "business" ? {
            company_name: formData.company_name,
            vat_number: formData.vat_number,
            fiscal_code: formData.fiscal_code,
            pec: formData.pec,
            sdi_code: formData.sdi_code,
            company_address: formData.company_address
          } : null,
          accountType === "private" ? formData.fiscal_code : (formData.fiscal_code || null),
          consentPrivacy,
          consentMarketing,
          hasPartnerCompany ? selectedPartnerCompanyId : null
        );
        toast.success(result.message);
        navigate("/profilo");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Errore durante l'operazione";
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset password functions
  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error("Inserisci la tua email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/password/request-reset`, { email: resetEmail });
      toast.success("Se l'email è registrata, riceverai un codice di reset");
      setResetStep(2);
    } catch (error) {
      toast.error("Errore nell'invio del codice");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetCode || !newPassword) {
      toast.error("Inserisci codice e nuova password");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/password/verify-reset`, {
        email: resetEmail,
        code: resetCode,
        new_password: newPassword
      });
      toast.success("Password aggiornata! Ora puoi accedere.");
      // Reset states
      setShowResetPassword(false);
      setResetStep(1);
      setResetEmail("");
      setResetCode("");
      setNewPassword("");
      setIsLogin(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Codice non valido o scaduto");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-black">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          
          {/* Reset Password UI */}
          {showResetPassword ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8">
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setResetStep(1);
                  setResetEmail("");
                  setResetCode("");
                  setNewPassword("");
                }}
                className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Torna al login
              </button>
              
              <div className="flex items-center justify-center gap-3 mb-6">
                <KeyRound className="w-8 h-8 text-green-500" />
                <h1 className="text-2xl font-bold text-white">Reset Password</h1>
              </div>

              {resetStep === 1 ? (
                <form onSubmit={handleRequestReset} className="space-y-4">
                  <p className="text-zinc-400 text-sm text-center mb-4">
                    Inserisci la tua email. Ti invieremo un codice di verifica.
                  </p>
                  <div>
                    <label className="block text-white font-semibold mb-2">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="email@esempio.it"
                        className="w-full bg-black border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-green-500"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-green-500 text-black py-3 rounded-lg font-bold hover:bg-green-600 transition-all disabled:bg-zinc-700 disabled:text-zinc-500 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      "Invia codice"
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="bg-green-500/10 border border-green-500 rounded-lg p-3 mb-4">
                    <p className="text-green-500 text-sm text-center">
                      Codice inviato a <strong>{resetEmail}</strong>
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-white font-semibold mb-2">Codice di verifica</label>
                    <input
                      type="text"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      maxLength={6}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-widest focus:outline-none focus:border-green-500"
                      required
                    />
                    <p className="text-zinc-500 text-xs mt-1 text-center">Inserisci il codice a 6 cifre ricevuto via email</p>
                  </div>
                  
                  <div>
                    <label className="block text-white font-semibold mb-2">Nuova password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Minimo 6 caratteri"
                        className="w-full bg-black border border-zinc-700 rounded-lg pl-10 pr-12 py-3 text-white focus:outline-none focus:border-green-500"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-green-500 text-black py-3 rounded-lg font-bold hover:bg-green-600 transition-all disabled:bg-zinc-700 disabled:text-zinc-500 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Verifica...
                      </>
                    ) : (
                      "Reimposta password"
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setResetStep(1)}
                    className="w-full text-zinc-400 hover:text-white text-sm transition-colors"
                  >
                    Non hai ricevuto il codice? Riprova
                  </button>
                </form>
              )}
            </div>
          ) : (
            <>
              {/* Bonus Banner */}
              {!isLogin && accountType === "private" && (
            <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-lg p-4 mb-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Gift className="w-6 h-6 text-yellow-300" />
                <span className="text-xl font-bold text-black">10 PUNTI IN REGALO!</span>
              </div>
              <p className="text-black/80 text-sm">Registrati ora e inizia a raccogliere punti</p>
            </div>
          )}

          {!isLogin && accountType === "business" && (
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg p-4 mb-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Building2 className="w-6 h-6 text-white" />
                <span className="text-xl font-bold text-white">ACCOUNT AZIENDALE</span>
              </div>
              <p className="text-white/80 text-sm">Pagando con carta: 5% monoporzione • 2,5% gastronomia</p>
            </div>
          )}

          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8">
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              {isLogin ? "Accedi" : "Registrati"}
            </h1>

            {/* Toggle */}
            <div className="flex bg-black rounded-lg p-1 mb-6">
              <button
                type="button"
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-2 rounded-lg font-semibold transition-all ${
                  isLogin ? "bg-green-500 text-black" : "text-zinc-400"
                }`}
              >
                Accedi
              </button>
              <button
                type="button"
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-2 rounded-lg font-semibold transition-all ${
                  !isLogin ? "bg-green-500 text-black" : "text-zinc-400"
                }`}
              >
                Registrati
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  {/* Scelta tipo account */}
                  <div>
                    <label className="block text-white font-semibold mb-2">Tipo account</label>
                    <div className="flex bg-black rounded-lg p-1">
                      <button
                        type="button"
                        onClick={() => setAccountType("private")}
                        className={`flex-1 py-2 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                          accountType === "private" ? "bg-green-500 text-black" : "text-zinc-400"
                        }`}
                      >
                        <User className="w-4 h-4" />
                        Privato
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccountType("business")}
                        className={`flex-1 py-2 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                          accountType === "business" ? "bg-green-500 text-black" : "text-zinc-400"
                        }`}
                      >
                        <Building2 className="w-4 h-4" />
                        Azienda
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">
                      {accountType === "business" ? "Nome referente" : "Nome completo"}
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder={accountType === "business" ? "Nome del referente" : "Mario Rossi"}
                        className="w-full bg-black border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-green-500"
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Campi aziendali */}
              {!isLogin && accountType === "business" && (
                <>
                  <div>
                    <label className="block text-white font-semibold mb-2">Ragione Sociale *</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <input
                        type="text"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleInputChange}
                        placeholder="Azienda S.r.l."
                        className="w-full bg-black border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-green-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white font-semibold mb-2">P.IVA *</label>
                      <input
                        type="text"
                        name="vat_number"
                        value={formData.vat_number}
                        onChange={handleInputChange}
                        placeholder="IT12345678901"
                        className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-white font-semibold mb-2">Codice Fiscale</label>
                      <input
                        type="text"
                        name="fiscal_code"
                        value={formData.fiscal_code}
                        onChange={handleInputChange}
                        placeholder="Codice fiscale"
                        className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white font-semibold mb-2">PEC</label>
                      <input
                        type="email"
                        name="pec"
                        value={formData.pec}
                        onChange={handleInputChange}
                        placeholder="azienda@pec.it"
                        className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-semibold mb-2">Codice SDI</label>
                      <input
                        type="text"
                        name="sdi_code"
                        value={formData.sdi_code}
                        onChange={handleInputChange}
                        placeholder="XXXXXXX"
                        maxLength={7}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Sede legale</label>
                    <input
                      type="text"
                      name="company_address"
                      value={formData.company_address}
                      onChange={handleInputChange}
                      placeholder="Via Roma 1, 00100 Roma (RM)"
                      className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-white font-semibold mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="email@esempio.it"
                    className="w-full bg-black border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-green-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-white font-semibold mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    className="w-full bg-black border border-zinc-700 rounded-lg pl-10 pr-12 py-3 text-white focus:outline-none focus:border-green-500"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(true)}
                    className="text-green-500 hover:text-green-400 text-sm mt-2 transition-colors"
                  >
                    Password dimenticata?
                  </button>
                )}
              </div>

              {!isLogin && (
                <div>
                  <label className="block text-white font-semibold mb-2">Telefono</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="333 1234567"
                      className="w-full bg-black border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-green-500"
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              {!isLogin && accountType === "private" && (
                <div>
                  <label className="block text-white font-semibold mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Data di nascita (per sconto compleanno!)
                  </label>
                  <input
                    type="date"
                    name="birthdate"
                    value={formData.birthdate}
                    onChange={handleInputChange}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                  />
                  <p className="text-zinc-500 text-sm mt-1">Riceverai il 5% di sconto nel periodo del tuo compleanno!</p>
                </div>
              )}

              {!isLogin && accountType === "private" && (
                <div>
                  <label className="block text-white font-semibold mb-2">Codice Fiscale *</label>
                  <input
                    type="text"
                    name="fiscal_code"
                    value={formData.fiscal_code}
                    onChange={(e) => setFormData({...formData, fiscal_code: e.target.value.toUpperCase()})}
                    placeholder="RSSMRA85M01H501Z"
                    maxLength={16}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white uppercase focus:outline-none focus:border-green-500"
                    required
                    data-testid="private-fiscal-code-input"
                  />
                  <p className="text-zinc-500 text-sm mt-1">Necessario per l'emissione della fattura</p>
                </div>
              )}

              {/* Azienda Partner */}
              {!isLogin && partnerCompanies.length > 0 && (
                <div className="border border-zinc-800 rounded-lg p-4">
                  <label className="flex items-center gap-3 cursor-pointer group" data-testid="partner-company-toggle">
                    <input
                      type="checkbox"
                      checked={hasPartnerCompany}
                      onChange={(e) => {
                        setHasPartnerCompany(e.target.checked);
                        if (!e.target.checked) setSelectedPartnerCompanyId("");
                      }}
                      className="w-5 h-5 rounded border-zinc-600 bg-black text-blue-500 focus:ring-blue-500 cursor-pointer accent-blue-500"
                    />
                    <span className="text-white font-semibold group-hover:text-blue-400 transition-colors flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-500" />
                      Appartieni a un'azienda partner?
                    </span>
                  </label>
                  {hasPartnerCompany && (
                    <div className="mt-3">
                      <select
                        value={selectedPartnerCompanyId}
                        onChange={(e) => setSelectedPartnerCompanyId(e.target.value)}
                        className={`w-full bg-black border rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 ${
                          hasPartnerCompany && !selectedPartnerCompanyId ? "border-yellow-500" : "border-zinc-700"
                        }`}
                        required
                        data-testid="partner-company-select"
                      >
                        <option value="">Seleziona azienda...</option>
                        {partnerCompanies.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      {!selectedPartnerCompanyId && (
                        <p className="text-yellow-500 text-sm mt-1">Seleziona l'azienda per completare la registrazione</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Consensi GDPR */}
              {!isLogin && (
                <div className="space-y-4 pt-2 border-t border-zinc-800">
                  {/* Consenso Privacy (obbligatorio) */}
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="privacy-consent-label">
                    <input
                      type="checkbox"
                      checked={consentPrivacy}
                      onChange={(e) => setConsentPrivacy(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-zinc-600 bg-black text-green-500 focus:ring-green-500 focus:ring-offset-0 cursor-pointer accent-green-500"
                      data-testid="privacy-consent-checkbox"
                    />
                    <span className="text-sm text-zinc-300 group-hover:text-white transition-colors">
                      Ho letto e accetto la{" "}
                      <Link to="/privacy-policy" target="_blank" className="text-green-500 underline hover:text-green-400" data-testid="privacy-policy-link">
                        Privacy Policy
                      </Link>
                      {" "}<span className="text-red-400">*</span>
                    </span>
                  </label>

                  {/* Consenso Marketing (facoltativo) */}
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="marketing-consent-label">
                    <input
                      type="checkbox"
                      checked={consentMarketing}
                      onChange={(e) => setConsentMarketing(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-zinc-600 bg-black text-green-500 focus:ring-green-500 focus:ring-offset-0 cursor-pointer accent-green-500"
                      data-testid="marketing-consent-checkbox"
                    />
                    <span className="text-sm text-zinc-300 group-hover:text-white transition-colors">
                      Accetto di ricevere comunicazioni promozionali (offerte, sconti e novità)
                    </span>
                  </label>
                </div>
              )}

              {/* Honeypot field - nascosto agli utenti, visibile ai bot */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                <label htmlFor="auth_website">Website</label>
                <input
                  type="text"
                  id="auth_website"
                  name="auth_website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-green-500 text-black py-3 rounded-lg font-bold text-lg hover:bg-green-600 transition-all disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Attendere...
                  </>
                ) : (
                  isLogin ? "Accedi" : "Registrati"
                )}
              </button>
            </form>

            {/* Benefits */}
            {!isLogin && (
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <p className="text-zinc-400 text-sm text-center mb-3">Perché registrarsi?</p>
                <ul className="text-zinc-300 text-sm space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Accumula 1 punto per ogni euro speso
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Riscatta prodotti omaggio
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Storico ordini sempre disponibile
                  </li>
                </ul>
              </div>
            )}
          </div>

          <p className="text-center text-zinc-500 mt-4">
            <Link to="/catalogo" className="text-green-500 hover:underline">
              ← Torna al catalogo
            </Link>
          </p>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CustomerAuthPage;
